import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { DIALOG_GUTTER } from '../../theme/shellMetrics';

/**
 * A form-field hint whose RULE is always visible and whose EXPLANATION is one click away.
 *
 * WHY THIS EXISTS
 * ---------------
 * The leave form's half-day hint ran three sentences, and measured across the nine locales it
 * swung from 63 characters (zh-CN) to 293 (fr) — roughly four lines under a control at the
 * long end, and seven or eight on a phone. A caption that reflows into a wall of text in some
 * languages and not others is not a translation problem to solve with shorter words; the
 * sentences were doing different JOBS:
 *
 *   1. the RULE the reader must know now            -> stays inline (10-39 chars everywhere)
 *   2. the ALTERNATIVE ROUTES, relevant to some     -> behind this disclosure
 *   3. the CONSEQUENCE of having no route           -> behind this disclosure
 *
 * Splitting by job rather than by length is what makes the inline line short in all nine
 * locales at once, instead of nine separate copy compromises.
 *
 * WHY A POPOVER AND NOT A TOOLTIP OR AN EXPANDER
 * ----------------------------------------------
 * A `Tooltip` is hover-first — it is unreachable on touch without a long-press, and MUI caps
 * its width so a paragraph wraps into a tall sliver. A `Collapse` ("read more") works but
 * pushes every control below it down, which is the layout jump the split was meant to remove.
 * A popover anchored on the icon costs no layout at all, opens on click and on keyboard, and
 * holds a paragraph comfortably.
 *
 * The popover's own padding reads `DIALOG_GUTTER`, so this surface sits on the app's single
 * horizontal axis rather than inventing a tenth inset (svdy-ui §17).
 */
export interface FieldHintProps {
  /** The short sentence the reader must see. Always rendered. */
  rule: string;
  /** The longer explanation. Rendered inside the popover. */
  detail: string;
  /** Accessible name for the trigger; defaults to the shared "more information" string. */
  triggerLabel?: string;
}

const FieldHint: React.FC<FieldHintProps> = ({ rule, detail, triggerLabel }) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {rule}
      </Typography>
      <IconButton
        size="small"
        // A real accessible name, not a decorative icon: the explanation is the only place
        // the alternative routes are stated, so a screen reader must be able to reach it.
        aria-label={triggerLabel ?? String(t('common:hint.moreInformation'))}
        onClick={(e) => setAnchor(e.currentTarget)}
        // Coloured and enlarged on owner review 2026-09-03: at 15px in `text.disabled` the
        // trigger read as decoration, and the alternative routes behind it are the only place
        // they are stated. The TEXT stays grey deliberately — this form already carries an
        // orange "still needed" line, and two high-contrast elements in one column is the
        // multiple-emphasis problem (svdy-ui §15).
        sx={{ p: 0.25, color: 'info.main' }}
      >
        <InfoOutlined sx={{ fontSize: 18 }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            // `&&` doubles the selector so this beats the theme's MuiDialog* overrides for
            // padding without a shorthand fighting them (svdy-ui §17).
            sx: {
              // An explicit width, not only a max: a popover paper sizes to its content, and a
              // paragraph with nowhere to go collapses.
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              // `paddingLeft/Right`, NOT the `px` shorthand. `DIALOG_GUTTER` is a raw PX value —
              // the theme uses it as `paddingLeft: DIALOG_GUTTER` — while `px:` in `sx` is a
              // spacing MULTIPLE, so `px: DIALOG_GUTTER` resolved to 24 x 8 = 192px per side.
              // That left ~1px for the text: it wrapped one word per line and stretched the page
              // by hundreds of pixels (owner report 2026-09-03). One constant, two unit systems.
              paddingLeft: `${DIALOG_GUTTER}px`,
              paddingRight: `${DIALOG_GUTTER}px`,
              py: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
            },
          },
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {detail}
        </Typography>
      </Popover>
    </Box>
  );
};

export default FieldHint;
